/**
 * StationRunList — the One Compass journey Navigator station view (v2.0 beat 2).
 *
 * Replaces the Cytoscape split-view pipeline (JourneyGraph) with the prototype's
 * horizontal stepper + station run list (docs/design/prototypes/02-journey-
 * navigator.html). No canvas graph: a status-colored stepper across the top and
 * a click-to-inspect run list below, each row showing the station's progress
 * line. Selecting a row drives the same StationActionPanel inspector the graph
 * used to.
 */
import React from 'react';
import { Check, CircleDot, Clock, Loader2, AlertTriangle } from 'lucide-react';
import type { Station } from '../../components/StationCard';
import './station-run-list.css';

interface StationRunListProps {
  stations: Station[];
  currentStationId: string | null;
  onStationSelect: (id: string) => void;
}

// Short labels for the stepper dots, keyed by station_id. Unknown stations fall
// back to the first two letters of their id.
const STEP_ABBR: Record<string, string> = {
  live_checks: 'FAIR',
  data_quality: 'DQ',
  ml_readiness: 'ML',
  ontology_inference: 'SC',
  contextual_validity: 'CV',
  documents: 'DOC',
  interviews: 'INT',
};

const STATION_LABEL: Record<string, string> = {
  live_checks: 'FAIR Core',
  data_quality: 'Data Quality',
  ml_readiness: 'ML Readiness',
  ontology_inference: 'Semantic Clarity',
  contextual_validity: 'Contextual Validity',
  documents: 'Document Analysis',
  interviews: 'Interviews',
};

function labelFor(s: Station): string {
  return s.name || STATION_LABEL[s.station_id] || s.station_id.replace(/_/g, ' ');
}

function abbrFor(s: Station): string {
  return STEP_ABBR[s.station_id] || s.station_id.slice(0, 2).toUpperCase();
}

function subFor(s: Station): string {
  if (s.status === 'completed') {
    const pct = s.completion_percentage;
    return typeof pct === 'number' ? `Complete · ${pct}%` : 'Complete';
  }
  if (s.status === 'in_progress') return 'Running…';
  if (s.status === 'failed') return s.error || 'Failed — review and retry';
  return s.description || 'Queued';
}

function rightFor(s: Station): string {
  switch (s.status) {
    case 'completed':
      return 'done';
    case 'in_progress':
      return 'running';
    case 'failed':
      return 'failed';
    default:
      return 'queued';
  }
}

const stepClass = (s: Station, isCurrent: boolean) => {
  if (s.status === 'completed') return 'srl-step done';
  if (s.status === 'in_progress' || isCurrent) return 'srl-step active';
  return 'srl-step';
};

export const StationRunList: React.FC<StationRunListProps> = ({
  stations,
  currentStationId,
  onStationSelect,
}) => {
  return (
    <div className="srl" data-testid="station-run-list">
      {/* horizontal stepper */}
      <div className="srl-stepper" role="list" aria-label="Station progress">
        {stations.map((s) => {
          const isCurrent = s.station_id === currentStationId;
          return (
            <button
              type="button"
              key={s.station_id}
              className={stepClass(s, isCurrent)}
              onClick={() => onStationSelect(s.station_id)}
              role="listitem"
              aria-current={isCurrent ? 'step' : undefined}
            >
              <span className="srl-step-dot">
                {s.status === 'completed' ? <Check size={16} aria-hidden="true" /> : abbrFor(s)}
              </span>
              <span className="srl-step-name">
                {labelFor(s)}
                {s.status === 'in_progress' && <small>running</small>}
              </span>
            </button>
          );
        })}
      </div>

      {/* run list */}
      <ul className="srl-list">
        {stations.map((s) => {
          const isCurrent = s.station_id === currentStationId;
          const rowClass =
            s.status === 'completed'
              ? 'srl-row done'
              : s.status === 'in_progress'
                ? 'srl-row active'
                : s.status === 'failed'
                  ? 'srl-row failed'
                  : 'srl-row';
          return (
            <li key={s.station_id}>
              <button
                type="button"
                className={`${rowClass}${isCurrent ? ' is-selected' : ''}`}
                onClick={() => onStationSelect(s.station_id)}
                data-testid={`srl-row-${s.station_id}`}
              >
                <span className="srl-row-icon" aria-hidden="true">
                  {s.status === 'completed' ? (
                    <Check size={18} className="srl-ic-done" />
                  ) : s.status === 'in_progress' ? (
                    <Loader2 size={18} className="srl-ic-active srl-spin" />
                  ) : s.status === 'failed' ? (
                    <AlertTriangle size={18} className="srl-ic-fail" />
                  ) : (
                    <Clock size={18} className="srl-ic-wait" />
                  )}
                </span>
                <span className="srl-row-body">
                  <span className="srl-row-name">{labelFor(s)}</span>
                  <span className="srl-row-sub">{subFor(s)}</span>
                </span>
                <span className="srl-row-right">{rightFor(s)}</span>
              </button>
            </li>
          );
        })}
        {stations.length === 0 && (
          <li className="srl-empty">
            <CircleDot size={16} aria-hidden="true" /> No stations on this journey yet.
          </li>
        )}
      </ul>
    </div>
  );
};

export default StationRunList;
